/**
 * Accua Cookie Consent - Accessible Modal Handler
 * 
 * This script handles the modal for all positions with full
 * EU Accessibility Act and WCAG 2.1 AA compliance.
 * 
 * Features:
 * - Focus trap (Tab/Shift+Tab cycles within modal)
 * - Escape key closes modal (same as Confirm Selection)
 * - Backdrop click triggers nudge animation (modal is persistent)
 * - X button hover highlights Confirm Selection button
 * - Background content made inert while modal is open
 * - Focus management: initial focus on modal, restore on close
 * - Two-view system (compact ↔ detail) with checkbox sync
 * 
 * Dependencies: jQuery, scripts.js (AccuaCookieConsent)
 * 
 * @since 5.0.0
 */
(function ($) {
    'use strict';

    /**
     * Accessible Modal Handler for all positions
     * 
     * @namespace AccuaAccessibleModal
     */
    const AccuaAccessibleModal = {
        modal: null,
        backdrop: null,
        triggerElement: null,
        focusableElements: [],
        firstFocusable: null,
        lastFocusable: null,
        isOpen: false,

        // Privacy modal elements
        privacyModal: null,
        privacyBackdrop: null,
        privacyTrigger: null,
        isPrivacyOpen: false,
        privacyContentLoaded: false,

        /**
         * Initialize the accessible modal
         */
        init: function () {
            this.modal = document.getElementById('accua-modal-modal');
            this.backdrop = document.getElementById('accua-modal-backdrop');

            // Only initialize if the modal exists (cimatti_a11y position)
            if (!this.modal) {
                return;
            }

            // Initialize privacy modal elements
            this.privacyModal = document.getElementById('accua-modal-privacy-modal');
            this.privacyBackdrop = document.getElementById('accua-modal-privacy-backdrop');

            this.setupEventListeners();
            this.setupMoreInfoToggle();
            this.setupPrivacyModal();
            this.setupOrientationHandler();

            // Show modal if no cookie exists
            if (typeof AccuaCookieConsent !== 'undefined' && 
                (AccuaCookieConsent.currentCookieValue === null ||
                 AccuaCookieConsent.currentCookieValue === "")) {
                this.open();
            }
        },

        /**
         * Setup all event listeners for the modal
         */
        setupEventListeners: function () {
            const self = this;

            // Accept All button
            $('#accua-modal-accept').on('click', function (e) {
                e.preventDefault();
                // Check all checkboxes
                self.modal.querySelectorAll('input[type="checkbox"]:not(:disabled)').forEach(function (cb) {
                    cb.checked = true;
                });
                self.confirmAndClose();
            });

            // Confirm Selection button
            $('#accua-modal-confirm').on('click', function (e) {
                e.preventDefault();
                self.confirmAndClose();
            });

            // Close (X) button - same action as Confirm Selection
            $('#accua-modal-close').on('click', function (e) {
                e.preventDefault();
                self.confirmAndClose();
            });

            // Backdrop click - nudge animation (don't close)
            $(this.backdrop).on('click', function (e) {
                e.preventDefault();
                self.nudge();
            });

            // Keyboard handling
            $(document).on('keydown', function (e) {
                if (!self.isOpen) return;
                
                // If privacy modal is open, don't handle keys for consent modal
                if (self.isPrivacyOpen) return;

                // Escape key - same action as Confirm Selection
                if (e.key === 'Escape' || e.keyCode === 27) {
                    e.preventDefault();
                    self.confirmAndClose();
                    return;
                }

                // Tab key - focus trap
                if (e.key === 'Tab' || e.keyCode === 9) {
                    self.handleTabKey(e);
                }
            });

            // X button hover/focus highlights Confirm Selection button
            $('#accua-modal-close').on('mouseenter focus', function () {
                $('#accua-modal-confirm').addClass('accua-modal-btn-highlighted');
            }).on('mouseleave blur', function () {
                $('#accua-modal-confirm').removeClass('accua-modal-btn-highlighted');
            });

            // Accept All hover/focus highlights unchecked checkboxes (if enabled)
            if (typeof accua_modal_data !== 'undefined' && accua_modal_data.acceptAllPreview === '1') {
                $('#accua-modal-accept').on('mouseenter focus', function () {
                    self.highlightUncheckedCategories(true);
                }).on('mouseleave blur', function () {
                    self.highlightUncheckedCategories(false);
                });
            }
        },

        /**
         * Highlight unchecked category checkboxes (visual preview for Accept All)
         */
        highlightUncheckedCategories: function (highlight) {
            const unchecked = this.modal.querySelectorAll('.accua-modal-checkbox:not(:checked):not(:disabled)');
            unchecked.forEach(function (cb) {
                const category = cb.closest('.accua-modal-category');
                if (category) {
                    if (highlight) {
                        category.classList.add('accua-modal-will-check');
                    } else {
                        category.classList.remove('accua-modal-will-check');
                    }
                }
            });
        },

        /**
         * Setup the More Info link and info buttons for view switching
         */
        setupMoreInfoToggle: function () {
            const self = this;
            
            // "Maggiori informazioni" / "Indietro" toggle link in footer
            $('#accua-modal-more-link').on('click', function (e) {
                e.preventDefault();
                const detailView = document.getElementById('accua-modal-detail-view');
                if (detailView && !detailView.hidden) {
                    self.showCompactView();
                } else {
                    self.showDetailView();
                }
            });

            // Setup info buttons (?) that switch to detail view and scroll to category
            $('.accua-modal-info-btn').on('click', function (e) {
                e.preventDefault();
                const category = $(this).data('category');
                self.showCategoryDetail(category);
            });

            // Sync checkboxes between compact and detail views
            this.setupCheckboxSync();
        },

        /**
         * Setup checkbox synchronization between footer checkboxes and detail view
         * 
         * The footer checkboxes (in .accua-modal-categories) are always visible,
         * while the detail view checkboxes are inside #accua-modal-detail-view.
         * We need to sync between these two locations.
         * 
         * OPTIMIZED: Uses native DOM APIs and pre-built checkbox map for O(1) lookups
         */
        setupCheckboxSync: function () {
            const self = this;
            const categoriesEl = document.querySelector('.accua-modal-categories');
            const detailViewEl = document.getElementById('accua-modal-detail-view');
            
            // Pre-build checkbox maps for O(1) lookup instead of DOM queries
            this.checkboxMap = { compact: {}, detail: {} };
            
            if (categoriesEl) {
                categoriesEl.querySelectorAll('.accua-modal-checkbox').forEach(function(cb) {
                    const name = cb.name || cb.value;
                    if (name) self.checkboxMap.compact[name] = cb;
                });
            }
            
            if (detailViewEl) {
                detailViewEl.querySelectorAll('.accua-modal-checkbox').forEach(function(cb) {
                    const name = cb.name || cb.value;
                    if (name) self.checkboxMap.detail[name] = cb;
                });
            }
            
            // Sync function using cached references (no DOM queries)
            function syncCheckbox(source, isFromCompact) {
                const name = source.name || source.value;
                if (!name) return;
                
                const target = isFromCompact ? self.checkboxMap.detail[name] : self.checkboxMap.compact[name];
                if (target && !target.disabled) {
                    target.checked = source.checked;
                }
            }
            
            // Event delegation with native DOM (faster than jQuery)
            if (categoriesEl) {
                categoriesEl.addEventListener('change', function(e) {
                    if (e.target.classList.contains('accua-modal-checkbox')) {
                        syncCheckbox(e.target, true);
                    }
                });
                
                // Row click to toggle checkbox
                categoriesEl.addEventListener('click', function(e) {
                    const category = e.target.closest('.accua-modal-category');
                    if (!category) return;
                    // Don't toggle if clicking checkbox, info button, or label (label[for] already toggles natively)
                    if (e.target.closest('.accua-modal-checkbox, .accua-modal-info-btn, label[for]')) return;
                    
                    const checkbox = category.querySelector('.accua-modal-checkbox');
                    if (checkbox && !checkbox.disabled) {
                        checkbox.checked = !checkbox.checked;
                        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
            }
            
            if (detailViewEl) {
                detailViewEl.addEventListener('change', function(e) {
                    if (e.target.classList.contains('accua-modal-checkbox')) {
                        syncCheckbox(e.target, false);
                    }
                });
                
                // Row click to toggle checkbox in detail view
                detailViewEl.addEventListener('click', function(e) {
                    const header = e.target.closest('.accua-modal-detail-header');
                    if (!header) return;
                    if (e.target.closest('.accua-modal-checkbox, label[for]')) return;
                    
                    const checkbox = header.querySelector('.accua-modal-checkbox');
                    if (checkbox && !checkbox.disabled) {
                        checkbox.checked = !checkbox.checked;
                        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
            }
        },

        /**
         * Show the detail view (full descriptions with checkboxes)
         */
        showDetailView: function (scrollToCategory) {
            const self = this;
            const compactView = document.getElementById('accua-modal-compact-view');
            const detailView = document.getElementById('accua-modal-detail-view');
            const moreLink = document.getElementById('accua-modal-more-link');

            if (!compactView || !detailView) return;

            // Use native classList for better performance
            compactView.classList.add('accua-modal-view-exiting');

            // Reduced delay for snappier transitions (50ms is enough for crossfade)
            setTimeout(function () {
                compactView.hidden = true;
                compactView.classList.remove('accua-modal-view-active', 'accua-modal-view-exiting');

                detailView.hidden = false;
                detailView.classList.add('accua-modal-view-active');

                // Add class to modal wrapper to indicate detail view is active
                self.modal.classList.add('accua-modal-detail-active');

                // Update link to show "Indietro" with back arrow
                if (moreLink) {
                    const backText = moreLink.getAttribute('data-back-text') || 'Indietro';
                    const textSpan = moreLink.querySelector('.accua-modal-link-text');
                    const iconSpan = moreLink.querySelector('.accua-modal-back-icon');
                    if (textSpan) textSpan.textContent = backText;
                    if (iconSpan) iconSpan.textContent = '← ';
                    moreLink.classList.add('accua-modal-showing-back');
                    moreLink.focus();
                }

                // Scroll to category immediately (no delay needed)
                if (scrollToCategory) {
                    self.scrollToCategory(scrollToCategory);
                }

                // Update focusable elements
                self.updateFocusableElements();

                // Announce to screen readers
                try {
                    const msg = (typeof accua_modal_data !== 'undefined' && accua_modal_data.sr && accua_modal_data.sr.showDetail) ? accua_modal_data.sr.showDetail : 'Showing detailed cookie information';
                    self.announce(msg);
                } catch (e) { /* noop */ }
            }, 50);
        },

        /**
         * Show the compact view (checkboxes only)
         */
        showCompactView: function () {
            const self = this;
            const compactView = document.getElementById('accua-modal-compact-view');
            const detailView = document.getElementById('accua-modal-detail-view');
            const moreLink = document.getElementById('accua-modal-more-link');

            if (!compactView || !detailView) return;

            // Use native classList for better performance
            detailView.classList.add('accua-modal-view-exiting');

            // Reduced delay for snappier transitions (50ms is enough for crossfade)
            setTimeout(function () {
                detailView.hidden = true;
                detailView.classList.remove('accua-modal-view-active', 'accua-modal-view-exiting');

                compactView.hidden = false;
                compactView.classList.add('accua-modal-view-active');

                // Remove class from modal wrapper when returning to compact view
                self.modal.classList.remove('accua-modal-detail-active');

                // Update link to show "Maggiori informazioni" again
                if (moreLink) {
                    const moreText = moreLink.getAttribute('data-more-text') || 'Maggiori informazioni';
                    const textSpan = moreLink.querySelector('.accua-modal-link-text');
                    const iconSpan = moreLink.querySelector('.accua-modal-back-icon');
                    if (textSpan) textSpan.textContent = moreText;
                    if (iconSpan) iconSpan.textContent = '';
                    moreLink.classList.remove('accua-modal-showing-back');
                    moreLink.focus();
                }

                // Update focusable elements
                self.updateFocusableElements();

                // Announce to screen readers
                try {
                    const msg = (typeof accua_modal_data !== 'undefined' && accua_modal_data.sr && accua_modal_data.sr.showCompact) ? accua_modal_data.sr.showCompact : 'Returned to cookie preferences';
                    self.announce(msg);
                } catch (e) { /* noop */ }
            }, 50);
        },

        /**
         * Scroll to a specific category in the detail view
         */
        scrollToCategory: function (categoryName) {
            const targetId = 'accua-modal-detail-' + categoryName;
            const targetSection = document.getElementById(targetId);

            if (!targetSection) return;

            // Scroll the section into view
            targetSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

            // Add highlight animation
            $(targetSection).addClass('accua-modal-highlight');

            // Remove highlight class after animation completes
            setTimeout(function () {
                $(targetSection).removeClass('accua-modal-highlight');
            }, 1100);
        },

        /**
         * Show a specific category's detail section (from ? button click)
         */
        showCategoryDetail: function (categoryName) {
            const detailView = document.getElementById('accua-modal-detail-view');

            // If already in detail view, just scroll to category
            if (detailView && !detailView.hidden) {
                this.scrollToCategory(categoryName);
            } else {
                // Switch to detail view and scroll to category
                this.showDetailView(categoryName);
            }
        },

        /**
         * Open the modal
         */
        open: function () {
            // Guard: if modal doesn't exist (e.g., admin users), do nothing
            if (!this.modal) return;
            
            if (this.isOpen) return;

            this.isOpen = true;
            this.triggerElement = document.activeElement;

            // Hide floating icon while modal is open
            if (typeof AccuaCookieConsent !== 'undefined' && AccuaCookieConsent.hideFloatingIcon) {
                AccuaCookieConsent.hideFloatingIcon();
            }

            // Make background inert
            this.setBackgroundInert(true);

            // Show modal and backdrop
            $(this.backdrop).addClass('accua-modal-active');
            $(this.modal).addClass('accua-modal-active');

            // Update focusable elements
            this.updateFocusableElements();

            // Set initial focus on the modal itself, then move to first interactive element
            this.modal.focus();

            // Announce to screen readers
            try {
                const msg = (typeof accua_modal_data !== 'undefined' && accua_modal_data.sr && accua_modal_data.sr.dialogOpened) ? accua_modal_data.sr.dialogOpened : 'Cookie consent dialog opened';
                this.announce(msg);
            } catch (e) { /* noop */ }
        },

        /**
         * Close the modal
         */
        close: function () {
            if (!this.modal || !this.isOpen) return;

            this.isOpen = false;

            // Hide modal and backdrop
            $(this.modal).removeClass('accua-modal-active');
            $(this.backdrop).removeClass('accua-modal-active');

            // Restore background
            this.setBackgroundInert(false);

            // Show floating icon again (with slight delay for animation)
            setTimeout(function () {
                if (typeof AccuaCookieConsent !== 'undefined' && AccuaCookieConsent.showFloatingIcon) {
                    AccuaCookieConsent.showFloatingIcon();
                }
                // Also un-hide if it was hidden for modal open
                var $icon = jQuery('.accua-cookie-floating-icon');
                if ($icon.length && !$icon.hasClass('accua-cookie-floating-icon--device-hidden')) {
                    $icon.removeClass('accua-cookie-floating-icon--hidden');
                }
            }, 150);

            // Restore focus to trigger element
            if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
                this.triggerElement.focus();
            }
        },

        /**
         * Confirm selection and close modal
         */
        confirmAndClose: function () {
            // Use the existing consent processing from AccuaCookieConsent
            if (typeof AccuaCookieConsent !== 'undefined') {
                AccuaCookieConsent.processConsent(true, "");
            }

            // Announce to screen readers
            try {
                const msg = (typeof accua_modal_data !== 'undefined' && accua_modal_data.sr && accua_modal_data.sr.prefsSaved) ? accua_modal_data.sr.prefsSaved : 'Cookie preferences saved';
                this.announce(msg);
            } catch (e) { /* noop */ }

            // Close the modal
            this.close();
        },

        /**
         * Trigger nudge animation when backdrop is clicked
         */
        nudge: function () {
            if (!this.modal) return;
            
            const self = this;

            // Add nudge class
            $(this.modal).addClass('accua-modal-nudge');

            // Remove class after animation
            setTimeout(function () {
                $(self.modal).removeClass('accua-modal-nudge');
            }, 300);

            // Focus the modal to draw attention
            this.modal.focus();
        },

        /**
         * Handle Tab key for focus trap
         */
        handleTabKey: function (e) {
            this.updateFocusableElements();

            if (this.focusableElements.length === 0) return;

            const activeElement = document.activeElement;

            if (e.shiftKey) {
                // Shift+Tab: if on first element, go to last
                if (activeElement === this.firstFocusable || activeElement === this.modal) {
                    e.preventDefault();
                    this.lastFocusable.focus();
                }
            } else {
                // Tab: if on last element, go to first
                if (activeElement === this.lastFocusable) {
                    e.preventDefault();
                    this.firstFocusable.focus();
                }
            }
        },

        /**
         * Update the list of focusable elements within the modal
         */
        updateFocusableElements: function () {
            if (!this.modal) return;
            
            const focusableSelectors = [
                'button:not([disabled])',
                'input:not([disabled])',
                'select:not([disabled])',
                'textarea:not([disabled])',
                'a[href]',
                '[tabindex]:not([tabindex="-1"])'
            ].join(', ');

            this.focusableElements = Array.from(
                this.modal.querySelectorAll(focusableSelectors)
            ).filter(function (el) {
                return el.offsetParent !== null; // Visible elements only
            });

            this.firstFocusable = this.focusableElements[0] || null;
            this.lastFocusable = this.focusableElements[this.focusableElements.length - 1] || null;
        },

        /**
         * Set background content as inert (non-interactive)
         */
        setBackgroundInert: function (inert) {
            // Get all direct children of body except the modal, backdrop, and WP admin bar
            const bodyChildren = document.body.children;

            for (let i = 0; i < bodyChildren.length; i++) {
                const child = bodyChildren[i];

                // Skip the modal, backdrop, live region, privacy modal, and WP admin bar
                if (child === this.modal ||
                    child === this.backdrop ||
                    child === this.privacyModal ||
                    child === this.privacyBackdrop ||
                    child.id === 'accua-modal-announcer' ||
                    child.id === 'wpadminbar') {
                    continue;
                }

                if (inert) {
                    // Store original values
                    child.dataset.gdprOriginalAriaHidden = child.getAttribute('aria-hidden') || '';
                    child.dataset.gdprOriginalInert = child.hasAttribute('inert') ? 'true' : 'false';

                    // Make inert
                    child.setAttribute('aria-hidden', 'true');
                    child.setAttribute('inert', '');
                } else {
                    // Restore original values
                    const originalAriaHidden = child.dataset.gdprOriginalAriaHidden;
                    const originalInert = child.dataset.gdprOriginalInert;

                    if (originalAriaHidden === '') {
                        child.removeAttribute('aria-hidden');
                    } else {
                        child.setAttribute('aria-hidden', originalAriaHidden);
                    }

                    if (originalInert !== 'true') {
                        child.removeAttribute('inert');
                    }

                    // Clean up data attributes
                    delete child.dataset.gdprOriginalAriaHidden;
                    delete child.dataset.gdprOriginalInert;
                }
            }
        },

        /**
         * Announce message to screen readers via live region
         */
        announce: function (message) {
            const announcer = document.getElementById('accua-modal-announcer');
            if (announcer) {
                announcer.textContent = message;
                // Clear after announcement
                setTimeout(function () {
                    announcer.textContent = '';
                }, 1000);
            }
        },

        /**
         * Setup privacy modal event listeners
         */
        setupPrivacyModal: function () {
            const self = this;

            // If privacy opens in new tab, the link is a regular <a> — no JS needed
            if (typeof accua_modal_data !== 'undefined' && accua_modal_data.privacyMode === 'newtab') {
                return;
            }

            // Privacy link in main modal
            $('#accua-modal-privacy-link').on('click', function (e) {
                e.preventDefault();
                self.privacyTrigger = this;
                self.openPrivacyModal();
            });

            if (!this.privacyModal) return;

            // Close button
            $('#accua-modal-privacy-close').on('click', function (e) {
                e.preventDefault();
                self.closePrivacyModal();
            });

            // Back button in footer
            $('#accua-modal-privacy-back').on('click', function (e) {
                e.preventDefault();
                self.closePrivacyModal();
            });

            // Backdrop click - close privacy modal (it's informational, not required)
            $(this.privacyBackdrop).on('click', function (e) {
                e.preventDefault();
                self.closePrivacyModal();
            });

            // Keyboard handling for privacy modal
            $(document).on('keydown.gdprPrivacy', function (e) {
                if (!self.isPrivacyOpen) return;

                // Escape key - close privacy modal
                if (e.key === 'Escape' || e.keyCode === 27) {
                    e.preventDefault();
                    e.stopPropagation();
                    self.closePrivacyModal();
                    return;
                }

                // Tab key - focus trap within privacy modal
                if (e.key === 'Tab' || e.keyCode === 9) {
                    self.handlePrivacyTabKey(e);
                }
            });
        },

        /**
         * Open the privacy policy modal
         */
        openPrivacyModal: function () {
            if (this.isPrivacyOpen || !this.privacyModal) return;

            this.isPrivacyOpen = true;

            // Remove inert/aria-hidden from privacy modal (may have been set by consent modal)
            this.privacyModal.removeAttribute('inert');
            this.privacyModal.removeAttribute('aria-hidden');
            this.privacyBackdrop.removeAttribute('inert');
            this.privacyBackdrop.removeAttribute('aria-hidden');

            // Show privacy backdrop and modal
            $(this.privacyBackdrop).addClass('accua-modal-active');
            $(this.privacyModal).removeAttr('hidden').addClass('accua-modal-active');

            // Make consent modal and its backdrop inert while privacy is open
            if (this.modal) {
                this.modal.setAttribute('inert', '');
                this.modal.setAttribute('aria-hidden', 'true');
            }
            if (this.backdrop) {
                // Hide consent backdrop so it doesn't intercept clicks
                $(this.backdrop).addClass('accua-modal-behind-privacy');
            }

            // Load content if not already loaded
            if (!this.privacyContentLoaded) {
                this.loadPrivacyContent();
            }

            // Focus the modal
            this.privacyModal.focus();

            // Announce to screen readers
            try {
                const msg = (typeof accua_modal_data !== 'undefined' && accua_modal_data.sr && accua_modal_data.sr.privacyOpened) ? accua_modal_data.sr.privacyOpened : 'Privacy policy opened';
                this.announce(msg);
            } catch (e) { /* noop */ }
        },

        /**
         * Close the privacy policy modal
         */
        closePrivacyModal: function () {
            if (!this.isPrivacyOpen) return;

            this.isPrivacyOpen = false;

            // Hide privacy backdrop and modal
            $(this.privacyBackdrop).attr('aria-hidden', 'true').removeClass('accua-modal-active');
            $(this.privacyModal).attr('hidden', '').removeClass('accua-modal-active');

            // Restore consent modal and its backdrop
            if (this.modal) {
                this.modal.removeAttribute('inert');
                this.modal.removeAttribute('aria-hidden');
            }
            if (this.backdrop) {
                $(this.backdrop).removeClass('accua-modal-behind-privacy');
            }

            // Return focus to trigger element
            if (this.privacyTrigger && typeof this.privacyTrigger.focus === 'function') {
                this.privacyTrigger.focus();
            }

            // Announce to screen readers
            try {
                const msg = (typeof accua_modal_data !== 'undefined' && accua_modal_data.sr && accua_modal_data.sr.backToConsent) ? accua_modal_data.sr.backToConsent : 'Returned to cookie consent';
                this.announce(msg);
            } catch (e) { /* noop */ }
        },

        /**
         * Load privacy policy content via AJAX
         */
        loadPrivacyContent: function () {
            const self = this;
            const loadingEl = document.getElementById('accua-modal-privacy-loading');
            const textEl = document.getElementById('accua-modal-privacy-text');
            const titleEl = document.getElementById('accua-modal-privacy-title');

            if (!textEl) return;

            // Check if accua_modal_data is available
            if (typeof accua_modal_data === 'undefined') {
                textEl.innerHTML = '<p>Error: Configuration not available.</p>';
                if (loadingEl) loadingEl.hidden = true;
                return;
            }

            // Show loading state
            if (loadingEl) loadingEl.hidden = false;
            textEl.innerHTML = '';

            // Check if this is an iframe-based privacy modal (same-domain custom URL)
            var iframeUrl = accua_modal_data.privacyIframeUrl || '';
            var privacyLink = document.getElementById('accua-modal-privacy-link');
            if (privacyLink && privacyLink.getAttribute('data-privacy-iframe')) {
                iframeUrl = privacyLink.getAttribute('data-privacy-iframe');
            }

            if (iframeUrl) {
                // Load content via iframe
                var bodyEl = document.getElementById('accua-modal-privacy-body');
                if (bodyEl) bodyEl.classList.add('accua-modal-privacy-body--iframe');

                var iframe = document.createElement('iframe');
                iframe.src = iframeUrl;
                iframe.className = 'accua-modal-privacy-iframe';
                iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-popups');
                iframe.setAttribute('title', titleEl ? titleEl.textContent : 'Privacy Policy');
                iframe.style.width = '100%';
                iframe.style.height = '100%';
                iframe.style.border = 'none';
                iframe.onload = function () {
                    if (loadingEl) loadingEl.hidden = true;
                    self.privacyContentLoaded = true;
                };
                iframe.onerror = function () {
                    if (loadingEl) loadingEl.hidden = true;
                    textEl.innerHTML = '<p>' + (accua_modal_data.errorText || 'Error loading content.') + '</p>';
                };
                textEl.appendChild(iframe);
                return;
            }

            // Make AJAX request (WordPress page source)
            $.ajax({
                url: accua_modal_data.ajaxUrl,
                type: 'POST',
                data: {
                    action: 'accua_get_privacy_content',
                        nonce: accua_modal_data.privacyNonce,
                        lang: (accua_modal_data && accua_modal_data.lang) ? accua_modal_data.lang : ''
                },
                success: function (response) {
                    if (loadingEl) loadingEl.hidden = true;

                    if (response.success && response.data) {
                        // Update title if provided
                        if (response.data.title && titleEl) {
                            titleEl.textContent = response.data.title;
                        }
                        // Insert content
                        textEl.innerHTML = response.data.content || '';
                        self.privacyContentLoaded = true;
                    } else {
                        textEl.innerHTML = '<p>' + (accua_modal_data.errorText || 'Error loading content.') + '</p>';
                    }
                },
                error: function () {
                    if (loadingEl) loadingEl.hidden = true;
                    textEl.innerHTML = '<p>' + (accua_modal_data.errorText || 'Error loading content.') + '</p>';
                }
            });
        },

        /**
         * Handle Tab key for privacy modal focus trap
         */
        handlePrivacyTabKey: function (e) {
            const focusableSelectors = [
                'button:not([disabled])',
                'a[href]',
                '[tabindex]:not([tabindex="-1"])'
            ].join(', ');

            const focusableElements = Array.from(
                this.privacyModal.querySelectorAll(focusableSelectors)
            ).filter(function (el) {
                return el.offsetParent !== null;
            });

            if (focusableElements.length === 0) return;

            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];
            const activeElement = document.activeElement;

            if (e.shiftKey) {
                // Shift+Tab: if on first element, go to last
                if (activeElement === firstFocusable || activeElement === this.privacyModal) {
                    e.preventDefault();
                    lastFocusable.focus();
                }
            } else {
                // Tab: if on last element, go to first
                if (activeElement === lastFocusable) {
                    e.preventDefault();
                    firstFocusable.focus();
                }
            }
        },

        /**
         * Setup orientation and resize handlers for responsive layout
         */
        setupOrientationHandler: function () {
            const self = this;
            let resizeTimeout;

            // Debounced resize handler
            function handleResize() {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(function () {
                    self.updateOrientationClass();
                    self.updateFocusableElements();
                }, 100);
            }

            // Add orientation class on init
            this.updateOrientationClass();

            // Listen for resize (covers orientation changes)
            window.addEventListener('resize', handleResize, { passive: true });
        },

        /**
         * Update orientation class on modal for CSS hooks
         */
        updateOrientationClass: function () {
            if (!this.modal) return;

            const isLandscape = window.innerWidth > window.innerHeight;
            const isCramped = window.innerHeight <= 500;

            this.modal.classList.toggle('accua-modal-landscape', isLandscape);
            this.modal.classList.toggle('accua-modal-cramped', isCramped);
        }
    };

    // Expose to global scope for use by other scripts
    window.AccuaAccessibleModal = AccuaAccessibleModal;
    // Legacy alias for backwards compatibility
    window.GDPRAccessibleModal = AccuaAccessibleModal;

    // Initialize on document ready
    $(document).ready(function () {
        AccuaAccessibleModal.init();
    });

})(jQuery);
